import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function QuoteForm() {
  const [formData, setFormData] = useState({
    site_name: '',
    htl_code: '',
    vendor: '',
    model: '',
    serial_numbers: '',
    renewal_type: '',
    quantity: '',
    ap_count: '',
    expiration_date: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    notes: ''
  });

  const [equipmentBreakdown, setEquipmentBreakdown] = useState([
    { model: '', qty: '' }
  ]);

  const [arubaLicenseTypes, setArubaLicenseTypes] = useState({
    foundation_care: false,
    pef_license: false,
    central_ap: false
  });

  const [screenshot, setScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEquipmentChange = (index, field, value) => {
    const newBreakdown = [...equipmentBreakdown];
    newBreakdown[index][field] = value;
    setEquipmentBreakdown(newBreakdown);
  };

  const addEquipmentRow = () => {
    setEquipmentBreakdown([...equipmentBreakdown, { model: '', qty: '' }]);
  };

  const removeEquipmentRow = (index) => {
    if (equipmentBreakdown.length > 1) {
      setEquipmentBreakdown(equipmentBreakdown.filter((_, i) => i !== index));
    }
  };

  const handleArubaLicenseChange = (type) => {
    setArubaLicenseTypes(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setScreenshot(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const data = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          data.append(key, formData[key]);
        }
      });

      if (formData.vendor === 'meraki') {
        data.append('equipment_breakdown', JSON.stringify(equipmentBreakdown));
      }

      if (formData.vendor === 'aruba') {
        data.append('aruba_license_types', JSON.stringify(arubaLicenseTypes));
      }

      if (screenshot) {
        data.append('screenshot', screenshot);
      }

      await axios.post(`${API_URL}/api/submit`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      site_name: '',
      htl_code: '',
      vendor: '',
      model: '',
      serial_numbers: '',
      renewal_type: '',
      quantity: '',
      ap_count: '',
      expiration_date: '',
      contact_name: '',
      contact_phone: '',
      contact_email: '',
      notes: ''
    });
    setEquipmentBreakdown([{ model: '', qty: '' }]);
    setArubaLicenseTypes({ foundation_care: false, pef_license: false, central_ap: false });
    setScreenshot(null);
    setSubmitted(false);
    setError('');
  };

  if (submitted) {
    return (
      <div className="main-content">
        <div className="card success-page">
          <div className="success-icon">✓</div>
          <h2>Quote Request Submitted!</h2>
          <p>Your renewal quote request has been successfully submitted to the SNA Renewals team.</p>
          <button className="btn btn-primary" onClick={resetForm}>
            Submit Another Request
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="header">
        <h1>SNA Renewals Quote Request</h1>
        <p>Complete the form below to request a renewal quote</p>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="card-header">
            <div className="card-icon">📍</div>
            <h2>Site Information</h2>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                Site Name <span className="required">*</span>
              </label>
              <input
                type="text"
                name="site_name"
                value={formData.site_name}
                onChange={handleChange}
                placeholder="e.g., SpringHill Suites Fresno"
                required
              />
            </div>

            <div className="form-group">
              <label>HTL Code / Site ID</label>
              <input
                type="text"
                name="htl_code"
                value={formData.htl_code}
                onChange={handleChange}
                placeholder="e.g., FATSH"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-icon">🔧</div>
            <h2>Equipment Details</h2>
          </div>

          <div className="form-group">
            <label>
              Vendor <span className="required">*</span>
            </label>
            <div className="radio-group">
              {['meraki', 'aruba', 'cisco', 'other'].map(vendor => (
                <label 
                  key={vendor}
                  className={`radio-item ${formData.vendor === vendor ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="vendor"
                    value={vendor}
                    checked={formData.vendor === vendor}
                    onChange={handleChange}
                    required
                  />
                  <span>{vendor.charAt(0).toUpperCase() + vendor.slice(1)}</span>
                </label>
              ))}
            </div>
          </div>

          {formData.vendor !== 'meraki' && formData.vendor && (
            <div className="form-row">
              <div className="form-group">
                <label>Model(s)</label>
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  placeholder="e.g., Aruba 7030"
                />
              </div>

              <div className="form-group">
                <label>Serial Number(s)</label>
                <textarea
                  name="serial_numbers"
                  value={formData.serial_numbers}
                  onChange={handleChange}
                  placeholder="Enter serial numbers (one per line)"
                  rows={3}
                />
              </div>
            </div>
          )}

          {formData.vendor === 'meraki' && (
            <div className="form-group">
              <label>Equipment License Breakdown</label>
              <div className="alert alert-info">
                <span>ℹ️</span>
                <span>For Meraki licensing, we don't need serial numbers but we do need the equipment breakdown below.</span>
              </div>
              <table className="equipment-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Quantity</th>
                    <th className="actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {equipmentBreakdown.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="text"
                          value={row.model}
                          onChange={(e) => handleEquipmentChange(index, 'model', e.target.value)}
                          placeholder="e.g., MX450"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row.qty}
                          onChange={(e) => handleEquipmentChange(index, 'qty', e.target.value)}
                          placeholder="Qty"
                          min="1"
                        />
                      </td>
                      <td className="actions">
                        <button
                          type="button"
                          className="btn-remove-row"
                          onClick={() => removeEquipmentRow(index)}
                          disabled={equipmentBreakdown.length === 1}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button type="button" className="btn-add-row" onClick={addEquipmentRow}>
                + Add Equipment Row
              </button>
            </div>
          )}

          {formData.vendor === 'aruba' && (
            <div className="form-group">
              <label>Aruba License Types Needed</label>
              <div className="checkbox-group">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={arubaLicenseTypes.foundation_care}
                    onChange={() => handleArubaLicenseChange('foundation_care')}
                  />
                  <div>
                    <span>HPE Foundation Care</span>
                    <small>Aruba Controller support (usually also needs PEF license per AP)</small>
                  </div>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={arubaLicenseTypes.pef_license}
                    onChange={() => handleArubaLicenseChange('pef_license')}
                  />
                  <div>
                    <span>PEF License</span>
                    <small>Aruba Controller Policy Enforcement Firewall license</small>
                  </div>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={arubaLicenseTypes.central_ap}
                    onChange={() => handleArubaLicenseChange('central_ap')}
                  />
                  <div>
                    <span>Central AP License</span>
                    <small>Aruba Central AP License (per AP)</small>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-icon">📋</div>
            <h2>Renewal Details</h2>
          </div>

          <div className="form-group">
            <label>
              Type of Renewal <span className="required">*</span>
            </label>
            <div className="radio-group">
              {['License', 'Warranty', 'Support'].map(type => (
                <label 
                  key={type}
                  className={`radio-item ${formData.renewal_type === type ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="renewal_type"
                    value={type}
                    checked={formData.renewal_type === type}
                    onChange={handleChange}
                    required
                  />
                  <span>{type}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Quantity</label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                placeholder="Enter quantity"
                min="1"
              />
            </div>

            <div className="form-group">
              <label>AP Count (if applicable)</label>
              <input
                type="number"
                name="ap_count"
                value={formData.ap_count}
                onChange={handleChange}
                placeholder="e.g., 609"
                min="0"
              />
            </div>

            <div className="form-group">
              <label>
                Current Expiration Date <span className="required">*</span>
              </label>
              <input
                type="date"
                name="expiration_date"
                value={formData.expiration_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-icon">👤</div>
            <h2>Point of Contact</h2>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Contact Name</label>
              <input
                type="text"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleChange}
                placeholder="e.g., Rebecca Kleim"
              />
            </div>

            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                name="contact_phone"
                value={formData.contact_phone}
                onChange={handleChange}
                placeholder="e.g., +1(559) 431-0004"
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="contact_email"
                value={formData.contact_email}
                onChange={handleChange}
                placeholder="e.g., contact@example.com"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-icon">📎</div>
            <h2>Additional Information</h2>
          </div>

          <div className="form-group">
            <label>Screenshot (Dashboard or License Info)</label>
            <div 
              className={`file-upload ${screenshot ? 'has-file' : ''}`}
              onClick={() => document.getElementById('screenshot-input').click()}
            >
              <input
                id="screenshot-input"
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange}
              />
              <div className="file-upload-icon">📷</div>
              {screenshot ? (
                <>
                  <p>File selected:</p>
                  <p className="file-name">{screenshot.name}</p>
                </>
              ) : (
                <>
                  <p>Click to upload a screenshot</p>
                  <p style={{ fontSize: '0.8125rem', color: '#9ca3af' }}>
                    PNG, JPG, or PDF (max 10MB)
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Additional Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Any additional information or special requests..."
              rows={4}
            />
          </div>
        </div>

        <button 
          type="submit" 
          className="btn btn-primary btn-full"
          disabled={submitting}
        >
          {submitting ? 'Submitting...' : 'Submit Quote Request'}
        </button>
      </form>

      <p style={{ textAlign: 'center', marginTop: '1.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
        <a href="/admin" style={{ color: '#3b82f6' }}>Admin Login</a>
      </p>
    </div>
  );
}

export default QuoteForm;
